import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { products as productsApi, type ProductAPI } from '../api/client';
import type { Product } from '../types';

/** Convert API snake_case product into frontend camelCase Product */
function mapProduct(p: ProductAPI): Product {
  return {
    id: p.id,
    name: p.name,
    brand: p.brand as Product['brand'],
    flavor: p.flavor,
    strength: p.strength,
    strengthUnit: p.strength_unit,
    price: p.price,
    originalPrice: p.original_price ?? undefined,
    description: p.description,
    longDescription: p.long_description ?? undefined,
    image: p.image ?? '/uploads/placeholder.png',
    inStock: p.in_stock,
    featured: p.featured,
    bestSeller: p.best_seller,
    isNew: p.is_new,
    rating: p.rating,
    reviewCount: p.review_count,
    pouchesPerCan: p.pouches_per_can,
    format: p.format,
    category: p.category as Product['category'],
    colorAccent: p.color_accent,
    stockQuantity: p.stock_quantity,
    maxOrderQty: p.max_order_qty,
    maxOrderQtyEnabled: p.max_order_qty_enabled,
  };
}

interface ProductsContextType {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getProduct: (id: string) => Product | undefined;
}

const ProductsContext = createContext<ProductsContextType | null>(null);

interface ProductsProviderProps {
  children: ReactNode;
  /** Server-side prefetched products (window.__INITIAL_DATA__.products).
   *  When present, skips the initial /api/products fetch so the SSR'd HTML
   *  and hydrated state agree without a network round-trip. */
  initialProducts?: unknown[];
}

export function ProductsProvider({ children, initialProducts }: ProductsProviderProps) {
  const hasSeed = Array.isArray(initialProducts) && initialProducts.length > 0;
  const [products, setProducts] = useState<Product[]>(
    hasSeed ? (initialProducts as Product[]) : []
  );
  const [isLoading, setIsLoading] = useState(!hasSeed);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setError(null);
      const res = await productsApi.list({ per_page: 100 });
      setProducts(res.items.map(mapProduct));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the initial fetch if SSR already seeded products.
    if (hasSeed) return;
    fetchProducts();
  }, [hasSeed, fetchProducts]);

  const getProduct = useCallback((id: string) => products.find(p => p.id === id), [products]);

  return (
    <ProductsContext.Provider value={{ products, isLoading, error, refetch: fetchProducts, getProduct }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error('useProducts must be used within ProductsProvider');
  return ctx;
}
